import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink, MessageSquare, Phone, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SideDrawer, SideDrawerContent, SideDrawerClose } from '@/components/ui/side-drawer'
import { ContactTagPicker } from '@/components/shared/contact-tag-picker'
import { ManageStagesLink } from '@/components/shared/manage-links'
import { useStageMove } from '@/hooks/use-stage-move'
import { apiError } from '@/lib/api-client'
import { formatVnd } from '@/lib/format-money'
import { cn, initials } from '@/lib/utils'
import { useLifecycleStages } from '@/hooks/use-lifecycle-stages'
import { useContact, useCreateNote, useUpdateContact, type ContactListItem } from '@/hooks/use-contacts'
import { MessageContactDialog } from './message-contact-dialog'
import { planMessage } from './message-contact-lib'

/** Xem nhanh một khách ngay trên danh sách: đổi giai đoạn, gắn nhãn, ghi chú nhanh — "Mở đầy đủ" mới sang trang chi tiết. */
export function ContactQuickView({ contact, onClose }: { contact: ContactListItem | null; onClose: () => void }) {
  const { t } = useTranslation('custDetail')
  const { stages } = useLifecycleStages()
  const update = useUpdateContact()
  const stageMove = useStageMove()
  const createNote = useCreateNote()
  const [note, setNote] = useState('')
  const navigate = useNavigate()
  const [msgOpen, setMsgOpen] = useState(false)
  // Hội thoại của khách lấy từ hồ sơ chi tiết (danh sách không kèm) — chỉ tải khi mở xem nhanh.
  const detail = useContact(contact?.id)
  const threads = detail.data?.conversations ?? []
  const plan = planMessage(threads)
  const onMessage = () => (plan.kind === 'direct' ? navigate(`/conversations/${plan.conversationId}`) : setMsgOpen(true))

  const name = contact ? contact.crmName || contact.fullName || t('quick.noName') : ''
  const setStage = (key: string) => {
    if (!contact || key === contact.lifecycleStage) return
    stageMove.move(contact.id, key, { name })
  }
  const saveNote = () => {
    const content = note.trim()
    if (!contact || !content) return
    createNote.mutate(
      { contactId: contact.id, content },
      {
        onSuccess: () => {
          setNote('')
          toast.success(t('quick.noteSaved'))
        },
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  return (
    <SideDrawer open={!!contact} onOpenChange={(o) => !o && onClose()}>
      <SideDrawerContent title={t('quick.title')} className="left-auto right-0 w-[92vw] max-w-md animate-fade-in">
        {contact && (
          <div className="flex h-full flex-col overflow-y-auto p-4">
            <div className="flex items-start gap-3">
              <Avatar className="h-12 w-12">
                {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} alt={name} />}
                <AvatarFallback>{initials(name)}</AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="min-w-0 truncate text-lg font-semibold">{name}</p>
                  <Button type="button" size="icon" variant="outline" className="h-7 w-7 shrink-0" onClick={onMessage} disabled={detail.isLoading} aria-label={t('quick.message')} title={t('quick.message')}>
                    <MessageSquare className="h-3.5 w-3.5" />
                  </Button>
                  <Button type="button" size="icon" variant="outline" className="h-7 w-7 shrink-0 opacity-60" disabled aria-label={t('quick.call')} title={t('quick.callSoon')}>
                    <Phone className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <p className="truncate text-sm text-muted-foreground">{[contact.phone, contact.email].filter(Boolean).join(' · ') || t('quick.noContact')}</p>
              </div>
              <SideDrawerClose asChild>
                <Button variant="ghost" size="icon" aria-label={t('common:actions.close')}>
                  <X className="h-4 w-4" />
                </Button>
              </SideDrawerClose>
            </div>

            <p className="mt-5 text-xs font-medium text-muted-foreground">{t('quick.stageHint')}</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {stages.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  disabled={update.isPending}
                  onClick={() => setStage(s.key)}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs transition-colors',
                    s.key === contact.lifecycleStage ? 'border-transparent font-semibold text-white' : 'text-muted-foreground hover:bg-muted',
                  )}
                  style={s.key === contact.lifecycleStage ? { backgroundColor: s.color ?? 'hsl(var(--primary))' } : undefined}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <ManageStagesLink />

            <p className="mt-4 text-xs font-medium text-muted-foreground">{t('quick.tags')}</p>
            <div className="mt-1.5">
              <ContactTagPicker contactId={contact.id} tags={contact.tags ?? []} />
            </div>

            <dl className="mt-5 grid grid-cols-[110px_1fr] gap-x-3 gap-y-2 text-sm">
              <dt className="text-muted-foreground">{t('quick.assignee')}</dt>
              <dd className="font-medium">{contact.assignedUser?.fullName || '—'}</dd>
              <dt className="text-muted-foreground">{t('quick.lastActivity')}</dt>
              <dd className="font-medium">{contact.lastActivity ? dayjs(contact.lastActivity).format('HH:mm DD/MM/YYYY') : '—'}</dd>
              <dt className="text-muted-foreground">{t('quick.totalSpent')}</dt>
              <dd className="font-medium tabular-nums">{formatVnd(contact.totalSpent ?? 0)}</dd>
              <dt className="text-muted-foreground">{t('quick.orderCount')}</dt>
              <dd className="font-medium tabular-nums">{contact.orderCount ?? 0}</dd>
            </dl>

            <p className="mt-5 text-xs font-medium text-muted-foreground">{t('quick.notes')}</p>
            <div className="mt-1.5 flex gap-2">
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveNote()}
                placeholder={t('quick.notePlaceholder')}
              />
              <Button variant="outline" disabled={!note.trim() || createNote.isPending} onClick={saveNote}>
                {t('common:actions.save')}
              </Button>
            </div>

            <Button asChild className="mt-auto w-full" size="lg">
              <Link to={`/customers/${contact.id}`}>
                {t('quick.openFull')} <ExternalLink className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}
        {contact && (
          <MessageContactDialog contactId={contact.id} name={name} conversations={threads} open={msgOpen} onOpenChange={setMsgOpen} />
        )}
      </SideDrawerContent>
    </SideDrawer>
  )
}
